import { c as _c } from "react/compiler-runtime";
import { IcoChevronLeftBig16, IcoSidebarLeftClosed16, IcoSidebarLeftOpen16 } from '@onefootprint/icons';
import { IconButton, Tooltip } from '@onefootprint/ui';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '@/components';
import FraudLabel from '@/components/fraud-label';
import Labels from '@/components/labels';
import ManualReview from '@/components/manual-review';
import PermissionGate from '@/components/permission-gate';
import Tags from '@/components/tags';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useInvestigationsListHref from '../../hooks/use-investigations-list-href';
import { ActionButtons, ActionsMenu } from './action-buttons';
import { IdentityRow } from './identity-row';
import InvestigationTitle from './investigation-title';
import type { HeaderProps } from './types';

// Two rows:
//   Row 1 (h-11): back + sidebar-toggle, centered title (or sub-investigations
//     dropdown), and investigation-level actions (Review user, Statuses, go-to-subject).
//   Row 2: identity (IDs, status, labels, fraud label, tags, run-by).
const DesktopHeader = props => {
  const $ = _c(39);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "header"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t0);
  const investigationsHref = useInvestigationsListHref();
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t(_temp2);
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IcoChevronLeftBig16 />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== investigationsHref || $[7] !== t2) {
    t4 = <Link href={investigationsHref} aria-label={t2} className="flex items-center justify-center size-7 rounded-(--icon-button-border-radius) text-primary hover:bg-hover-control transition-colors no-underline">{t3}</Link>;
    $[6] = investigationsHref;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <Tooltip content={t1}>{t4}</Tooltip>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== props.isSidebarOpen || $[13] !== props.onToggleSidebar || $[14] !== t) {
    t6 = props.onToggleSidebar && <Tooltip content={t($_1 => props.isSidebarOpen ? $_1.sidebarToggle.hide : $_1.sidebarToggle.show)}><IconButton aria-label={t($_2 => props.isSidebarOpen ? $_2.sidebarToggle.hide : $_2.sidebarToggle.show)} icon={props.isSidebarOpen ? IcoSidebarLeftOpen16 : IcoSidebarLeftClosed16} size="compact" variant="ghost" onClick={props.onToggleSidebar} /></Tooltip>;
    $[12] = props.isSidebarOpen;
    $[13] = props.onToggleSidebar;
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <div className="flex shrink-0 items-center gap-1">{t5}{t6}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== props.subInvestigations) {
    t8 = props.subInvestigations ?? [];
    $[19] = props.subInvestigations;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = !!props.hasMoreSubInvestigations;
  let t10;
  if ($[21] !== props.investigation || $[22] !== props.parentInvestigation || $[23] !== props.subInvestigationsParentId || $[24] !== t8 || $[25] !== t9) {
    t10 = <div className="absolute left-1/2 -translate-x-1/2 max-w-[40%] flex justify-center"><InvestigationTitle investigation={props.investigation} subInvestigations={t8} hasMore={t9} parentId={props.subInvestigationsParentId} parentInvestigation={props.parentInvestigation} /></div>;
    $[21] = props.investigation;
    $[22] = props.parentInvestigation;
    $[23] = props.subInvestigationsParentId;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== props) {
    t11 = <div id="investigation-header-actions" className="ml-auto flex flex-wrap items-center justify-end gap-2 shrink-0 max-w-full"><ActionButtons {...props} /></div>;
    $[27] = props;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t7) {
    t12 = <div className="relative flex flex-wrap items-center gap-2 px-3 h-11">{t7}{t10}{t11}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t7;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== props.entity || $[34] !== props.investigation) {
    t13 = <div className="px-3 pt-1 pb-3.5"><IdentityRow investigation={props.investigation} entity={props.entity} /></div>;
    $[33] = props.entity;
    $[34] = props.investigation;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t12 || $[37] !== t13) {
    t14 = <header className="shrink-0 border-b border-tertiary bg-primary">{t12}{t13}</header>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
};

// On a phone the secondary actions fold into one `…` menu and the document title switches artifacts,
// which leaves the subject's status and the review decision to share one row.
const MobileHeader = props => {
  const $ = _c(26);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "header"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("investigations", t0);
  const investigationsHref = useInvestigationsListHref();
  const {
    entity
  } = props;
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp3);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <IcoChevronLeftBig16 />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== investigationsHref || $[5] !== t1) {
    t3 = <Link href={investigationsHref} aria-label={t1} className="flex items-center justify-center size-11 shrink-0 rounded-(--icon-button-border-radius) text-primary hover:bg-hover-control active:bg-hover-control transition-colors no-underline">{t2}</Link>;
    $[4] = investigationsHref;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props.subInvestigations) {
    t4 = props.subInvestigations ?? [];
    $[7] = props.subInvestigations;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = !!props.hasMoreSubInvestigations;
  let t6;
  if ($[9] !== props.investigation || $[10] !== props.parentInvestigation || $[11] !== props.subInvestigationsParentId || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex min-w-0 flex-1"><InvestigationTitle investigation={props.investigation} subInvestigations={t4} hasMore={t5} parentId={props.subInvestigationsParentId} parentInvestigation={props.parentInvestigation} /></div>;
    $[9] = props.investigation;
    $[10] = props.parentInvestigation;
    $[11] = props.subInvestigationsParentId;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== props) {
    t7 = <ActionsMenu {...props} />;
    $[15] = props;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t3 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className="flex items-center gap-1 px-2 h-12">{t3}{t6}{t7}</div>;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== entity) {
    t9 = entity && <div className="flex items-center gap-3 px-4 pb-2 group-data-[keyboard-open]/app:hidden"><div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto pr-6 [scrollbar-width:none] [mask-image:linear-gradient(to_right,#000_calc(100%-1.5rem),transparent)] *:shrink-0"><StatusBadge status={entity.status} requiresManualReview={false} /><FraudLabel hideWhenEmpty={true} /><Labels entity={entity} /><Tags /></div><PermissionGate scopeKind="manual_review" fallbackText=""><ManualReview status={entity.status} kind={entity.kind} size="default" /></PermissionGate></div>;
    $[21] = entity;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8 || $[24] !== t9) {
    t10 = <header className="shrink-0 border-b border-tertiary bg-primary">{t8}{t9}</header>;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};

// The investigation's global header. Document-specific actions live in the per-document ContentHeader.
export const Header = props => {
  const $ = _c(3);
  const isMobile = useMobileLayout();
  let t0;
  if ($[0] !== isMobile || $[1] !== props) {
    t0 = isMobile ? <MobileHeader {...props} /> : <DesktopHeader {...props} />;
    $[0] = isMobile;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
function _temp($) {
  return $.back;
}
function _temp2($_0) {
  return $_0.back;
}
function _temp3($) {
  return $.back;
}